import React, { useState } from "react";
import {
  Card,
  DatePicker,
  Button,
  Table,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
} from "antd";
import {
  DownloadOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { useCustom } from "@refinedev/core";
import type {
  RegularizedSurfaceReport as ReportData,
  RegularizedSurfaceReportFilters,
  RegularizedSurfaceByYear,
  RegularizedSurfaceByMonth,
} from "../../types/incidence";
import dayjs, { Dayjs } from "dayjs";
// @ts-ignore - xlsx types are not fully compatible
import * as XLSX from "xlsx";
// @ts-ignore - jspdf types are not fully compatible
import jsPDF from "jspdf";
// @ts-ignore - jspdf-autotable types are not fully compatible
import autoTable from "jspdf-autotable";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

export const RegularizedSurfaceReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null,
  ]);

  // Build query params for the API
  const queryParams: RegularizedSurfaceReportFilters = {};
  if (dateRange[0]) {
    queryParams.start_date = dateRange[0].format("YYYY-MM-DD");
  }
  if (dateRange[1]) {
    queryParams.end_date = dateRange[1].format("YYYY-MM-DD");
  }

  // Fetch report data
  const { data, isLoading, refetch } = useCustom<ReportData>({
    url: "/incidences/regularized-surface-report/",
    method: "get",
    config: {
      query: queryParams,
    },
  }) as any; // Type assertion for compatibility

  const reportData = data?.data;

  // Handle date range change
  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates) {
      setDateRange(dates);
    } else {
      setDateRange([null, null]);
    }
  };

  // Apply filters
  const handleApplyFilters = () => {
    refetch();
  };

  // Clear filters
  const handleClearFilters = () => {
    setDateRange([null, null]);
    setTimeout(() => refetch(), 0);
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (!reportData) return;

    const wb = XLSX.utils.book_new();

    // By Year sheet
    const yearData = reportData.by_year.map((item: RegularizedSurfaceByYear) => ({
      Año: dayjs(item.year).format("YYYY"),
      "Superficie Total (ha)": item.total_surface?.toFixed(2) || "0.00",
      "Cantidad de Incidencias": item.count,
    }));
    const wsYear = XLSX.utils.json_to_sheet(yearData);
    XLSX.utils.book_append_sheet(wb, wsYear, "Por Año");

    // By Month sheet
    const monthData = reportData.by_month.map((item: RegularizedSurfaceByMonth) => ({
      Mes: dayjs(item.month).format("MMMM YYYY"),
      "Superficie Total (ha)": item.total_surface?.toFixed(2) || "0.00",
      "Cantidad de Incidencias": item.count,
    }));
    const wsMonth = XLSX.utils.json_to_sheet(monthData);
    XLSX.utils.book_append_sheet(wb, wsMonth, "Por Mes");

    // Total sheet
    const totalData = [
      {
        Concepto: "Total General",
        "Superficie Total (ha)":
          reportData.total.total_surface?.toFixed(2) || "0.00",
        "Cantidad de Incidencias": reportData.total.count,
      },
    ];
    const wsTotal = XLSX.utils.json_to_sheet(totalData);
    XLSX.utils.book_append_sheet(wb, wsTotal, "Total");

    // Generate filename with date range
    let filename = "reporte_superficie_regularizada";
    if (dateRange[0] && dateRange[1]) {
      filename += `_${dateRange[0].format("YYYY-MM-DD")}_${dateRange[1].format("YYYY-MM-DD")}`;
    }
    filename += ".xlsx";

    XLSX.writeFile(wb, filename);
  };

  // Export to PDF
  const handleExportPDF = () => {
    if (!reportData) return;

    const doc = new jsPDF();

    // Title
    doc.setFontSize(18);
    doc.text("Reporte de Superficie Regularizada", 14, 20);

    // Date range
    if (dateRange[0] && dateRange[1]) {
      doc.setFontSize(12);
      doc.text(
        `Período: ${dateRange[0].format("DD/MM/YYYY")} - ${dateRange[1].format("DD/MM/YYYY")}`,
        14,
        30
      );
    }

    // By Year table
    doc.setFontSize(14);
    doc.text("Agregación por Año", 14, 45);

    const yearTableData = reportData.by_year.map((item: RegularizedSurfaceByYear) => [
      dayjs(item.year).format("YYYY"),
      item.total_surface?.toFixed(2) || "0.00",
      item.count.toString(),
    ]);

    autoTable(doc, {
      startY: 50,
      head: [["Año", "Superficie Total (ha)", "Cantidad"]],
      body: yearTableData,
    });

    // By Month table
    const finalYAfterYear = (doc as any).lastAutoTable.finalY || 50;
    doc.setFontSize(14);
    doc.text("Agregación por Mes", 14, finalYAfterYear + 15);

    const monthTableData = reportData.by_month.map((item: RegularizedSurfaceByMonth) => [
      dayjs(item.month).format("MMMM YYYY"),
      item.total_surface?.toFixed(2) || "0.00",
      item.count.toString(),
    ]);

    autoTable(doc, {
      startY: finalYAfterYear + 20,
      head: [["Mes", "Superficie Total (ha)", "Cantidad"]],
      body: monthTableData,
    });

    // Total
    const finalYAfterMonth = (doc as any).lastAutoTable.finalY || 100;
    doc.setFontSize(14);
    doc.text("Total General", 14, finalYAfterMonth + 15);

    autoTable(doc, {
      startY: finalYAfterMonth + 20,
      head: [["Concepto", "Superficie Total (ha)", "Cantidad"]],
      body: [
        [
          "Total",
          reportData.total.total_surface?.toFixed(2) || "0.00",
          reportData.total.count.toString(),
        ],
      ],
    });

    // Generate filename with date range
    let filename = "reporte_superficie_regularizada";
    if (dateRange[0] && dateRange[1]) {
      filename += `_${dateRange[0].format("YYYY-MM-DD")}_${dateRange[1].format("YYYY-MM-DD")}`;
    }
    filename += ".pdf";

    doc.save(filename);
  };

  // Table columns for by year
  const yearColumns = [
    {
      title: "Año",
      dataIndex: "year",
      key: "year",
      render: (year: string) => dayjs(year).format("YYYY"),
    },
    {
      title: "Superficie Total (ha)",
      dataIndex: "total_surface",
      key: "total_surface",
      render: (value: number) => value?.toFixed(2) || "0.00",
    },
    {
      title: "Cantidad de Incidencias",
      dataIndex: "count",
      key: "count",
    },
  ];

  // Table columns for by month
  const monthColumns = [
    {
      title: "Mes",
      dataIndex: "month",
      key: "month",
      render: (month: string) => dayjs(month).format("MMMM YYYY"),
    },
    {
      title: "Superficie Total (ha)",
      dataIndex: "total_surface",
      key: "total_surface",
      render: (value: number) => value?.toFixed(2) || "0.00",
    },
    {
      title: "Cantidad de Incidencias",
      dataIndex: "count",
      key: "count",
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Title level={2}>
        <CalendarOutlined /> Reporte de Superficie Regularizada
      </Title>

      {/* Filters Card */}
      <Card
        title="Filtros"
        style={{ marginBottom: 24 }}
        extra={
          <Space>
            <Button onClick={handleClearFilters}>Limpiar</Button>
            <Button type="primary" onClick={handleApplyFilters}>
              Aplicar Filtros
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text>Rango de Fechas:</Text>
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            format="DD/MM/YYYY"
            style={{ width: "100%" }}
            placeholder={["Fecha inicio", "Fecha fin"]}
          />
        </Space>
      </Card>

      {/* Total Statistics */}
      {reportData && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Card>
              <Statistic
                title="Superficie Total Regularizada"
                value={reportData.total.total_surface?.toFixed(2) || "0.00"}
                suffix="ha"
                precision={2}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card>
              <Statistic
                title="Total de Incidencias"
                value={reportData.total.count}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Export Buttons */}
      <Card style={{ marginBottom: 24 }}>
        <Space>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={handleExportExcel}
            disabled={!reportData || isLoading}
          >
            Exportar a Excel
          </Button>
          <Button
            icon={<FilePdfOutlined />}
            onClick={handleExportPDF}
            disabled={!reportData || isLoading}
          >
            Exportar a PDF
          </Button>
        </Space>
      </Card>

      {/* By Year Table */}
      <Card title="Agregación por Año" style={{ marginBottom: 24 }}>
        <Table
          dataSource={reportData?.by_year || []}
          columns={yearColumns}
          loading={isLoading}
          rowKey="year"
          pagination={false}
        />
      </Card>

      {/* By Month Table */}
      <Card title="Agregación por Mes">
        <Table
          dataSource={reportData?.by_month || []}
          columns={monthColumns}
          loading={isLoading}
          rowKey="month"
          pagination={{ pageSize: 12 }}
        />
      </Card>
    </div>
  );
};
